// Shared state + submit logic for the bug-report panel.
//
// Owns description / submitting / submit-error / "done" / queueForDevSync /
// repoPath / graphicFile / myReports list state. Both the desktop floating
// pill (`BugReportButton`) and the mobile sandwich-menu entry
// (`BugReportMobileEntry`) construct one of these hooks per mount so they
// have parity for compose + submit + open-ticket count, without forcing
// the desktop and mobile chrome to share a single React tree.
//
// `loadOnOpenKey` is a monotonic counter (or `open` boolean cast to number)
// that triggers a fresh "my reports" reload — the hook itself doesn't own
// the open/closed state because the trigger pattern differs between
// desktop (pill toggle) and mobile (modal open).

import { useCallback, useEffect, useState } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  canQueueDevSyncNotes,
  isPlatformOwnerForDevSync,
} from "@/lib/devActionableNotes";
import {
  devNotesAssetUpload,
  fileToBase64,
} from "@/lib/devActionableNotesClient";
import {
  bugReportApiFetch,
  type BugReport,
} from "@/components/common/bugReportClient";
import { buildBugReportPayload } from "@/components/common/bugReportPayload";

export type UseBugReportPanelArgs = {
  mode: PrimaryMode;
  /**
   * Set to a truthy value when the panel becomes visible. Each change
   * triggers a reload of "my reports". The exact value doesn't matter
   * (number/bool/string) — only that the reference changes.
   */
  loadOnOpenKey: number | string | boolean;
};

export type UseBugReportPanelResult = {
  isAuth: boolean;
  canQueueSync: boolean;
  isOwnerSync: boolean;
  description: string;
  setDescription: (next: string) => void;
  submitting: boolean;
  submitError: string | null;
  submitDone: boolean;
  submitDoneDevSync: boolean;
  queueForDevSync: boolean;
  setQueueForDevSync: (next: boolean) => void;
  repoPath: string;
  setRepoPath: (next: string) => void;
  setGraphicFile: (file: File | null) => void;
  myReports: BugReport[];
  reportsLoading: boolean;
  openCount: number;
  submitReport: () => Promise<void>;
  appendDictation: (chunk: string) => void;
};

export function useBugReportPanel({
  mode,
  loadOnOpenKey,
}: UseBugReportPanelArgs): UseBugReportPanelResult {
  const auth = useAuthOptional();
  const scopedOrganizationId = useScopedOrganizationId();

  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitDone, setSubmitDone] = useState(false);
  const [submitDoneDevSync, setSubmitDoneDevSync] = useState(false);
  const [queueForDevSync, setQueueForDevSync] = useState(true);
  const [repoPath, setRepoPath] = useState("");
  const [graphicFile, setGraphicFile] = useState<File | null>(null);
  const [myReports, setMyReports] = useState<BugReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const isAuth = !!auth?.session;
  const canQueueSync = canQueueDevSyncNotes(auth);
  const isOwnerSync = isPlatformOwnerForDevSync(auth);
  const openCount = myReports.filter(
    (r) => r.status === "open" || r.status === "in_progress",
  ).length;

  // Dictation appends incoming transcript chunks. Functional setState keeps
  // this callback identity-stable so the mic engine's `onAppendText`
  // (registered once at engine construction) never sees a stale closure.
  const appendDictation = useCallback((chunk: string) => {
    const trimmed = chunk.trim();
    if (!trimmed) return;
    setDescription((prev) => (prev ? prev.replace(/\s+$/, "") + " " + trimmed : trimmed));
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!auth?.session) return null;
    return (auth.session as unknown as { access_token?: string }).access_token ?? null;
  }, [auth?.session]);

  const loadMyReports = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setReportsLoading(true);
    const result = await bugReportApiFetch<{ reports: BugReport[] }>(
      "/api/bugs/mine",
      "POST",
      token,
    );
    if (result.ok) setMyReports(result.data.reports);
    setReportsLoading(false);
  }, [getToken]);

  const submitReport = useCallback(async () => {
    if (!description.trim()) return;
    const token = await getToken();
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    // platform_owner always queues (server enforces too — dev-sync 89692b9f).
    const wantDevSync = canQueueSync && (isOwnerSync || queueForDevSync);
    const { payload, placement } = buildBugReportPayload({
      description,
      mode,
      repoPath,
      auth,
      scopedOrganizationId,
      queueForDevSync: wantDevSync,
    });

    const result = await bugReportApiFetch<{
      dev_note_id?: string;
      needs_review?: boolean;
      dev_sync_only?: boolean;
    }>("/api/bugs/report", "POST", token, payload);
    if (!result.ok) {
      setSubmitting(false);
      setSubmitError(result.error);
      return;
    }

    const noteId = result.data.dev_note_id;
    const wasDevSync = !!result.data.dev_sync_only || !!noteId;
    if (noteId && graphicFile) {
      try {
        const content_base64 = await fileToBase64(graphicFile);
        const up = await devNotesAssetUpload(token, {
          id: noteId,
          content_base64,
          content_type: graphicFile.type || "image/svg+xml",
          placement,
        });
        if (!up.ok) {
          setSubmitting(false);
          setSubmitError(`Note saved but graphic upload failed: ${up.error}`);
          return;
        }
      } catch (e) {
        setSubmitting(false);
        setSubmitError(e instanceof Error ? e.message : "Graphic upload failed");
        return;
      }
    }

    setSubmitting(false);
    setDescription("");
    setRepoPath("");
    setGraphicFile(null);
    setSubmitDoneDevSync(wasDevSync);
    setSubmitDone(true);
    void loadMyReports();
    setTimeout(() => {
      setSubmitDone(false);
      setSubmitDoneDevSync(false);
    }, 4000);
  }, [
    auth,
    canQueueSync,
    description,
    getToken,
    graphicFile,
    isOwnerSync,
    loadMyReports,
    mode,
    queueForDevSync,
    repoPath,
    scopedOrganizationId,
  ]);

  // Reload reports each time the panel becomes visible. Caller flips the
  // `loadOnOpenKey` to a new value (boolean ↔ boolean is fine — false→true
  // counts as a change).
  useEffect(() => {
    if (!loadOnOpenKey || !isAuth) return;
    void loadMyReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadOnOpenKey, isAuth]);

  return {
    isAuth,
    canQueueSync,
    isOwnerSync,
    description,
    setDescription,
    submitting,
    submitError,
    submitDone,
    submitDoneDevSync,
    queueForDevSync,
    setQueueForDevSync,
    repoPath,
    setRepoPath,
    setGraphicFile,
    myReports,
    reportsLoading,
    openCount,
    submitReport,
    appendDictation,
  };
}
